import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Textarea } from '@repo/ui/textarea'
import { onInput } from '@repo/ui/utils'
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

import { storeActions } from '@/store/actions'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

const MAX_HEIGHT = 360
export function ChatInput({ isDisabled }: { isDisabled: boolean }) {
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'admin.insights.chat' })
  const generationId = useStoreState((state) => state.assistant.generationId)

  const [input, setInput] = useState<string>('')
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  useAutosizeTextArea(textAreaRef, input, MAX_HEIGHT)

  const isEmpty = isEmptyString(input)

  const handleSubmit = () => {
    if (!generationId || isEmpty || isDisabled) return

    void dispatch(
      storeActions.assistant.askFor.beneficiaries.information({
        generationId,
        input,
      }),
    )
    setInput('')
  }

  useEffect(() => {
    if (isDisabled) return
    textAreaRef.current?.focus()
  }, [isDisabled])

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        ref={textAreaRef}
        className="placeholder:text-primary-400 text-primary bg- select-none resize-none overflow-visible rounded-lg p-4 text-base placeholder:select-none"
        placeholder={t('placeholder')}
        value={input}
        onChange={onInput(setInput)}
        onKeyDown={(e) => {
          if (e.metaKey && e.key === 'Enter') {
            handleSubmit()
          }
        }}
        style={{ maxHeight: `${MAX_HEIGHT}px` }}
        disabled={isDisabled}
        autoFocus
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="highlight"
          className="w-fit"
          onClick={handleSubmit}
          disabled={isDisabled || isEmpty}
        >
          <IoSparklesSharp className="size-4" />
          {t('cta')}
        </Button>
      </div>
    </div>
  )
}

const useAutosizeTextArea = (
  textAreaRef: RefObject<HTMLTextAreaElement>,
  value: string,
  maxHeight?: number,
) => {
  useLayoutEffect(() => {
    const textArea = textAreaRef.current
    if (textArea) {
      // Temporarily shrink the textarea to calculate the new scroll height
      textArea.style.height = 'auto'
      const scrollHeight = textArea.scrollHeight

      // If a maxHeight is set and the scrollHeight exceeds it, cap the height and enable scrolling
      if (maxHeight && scrollHeight > maxHeight) {
        textArea.style.height = `${maxHeight}px`
        textArea.style.overflowY = 'auto'
      } else {
        // Otherwise, set the height to the scroll height and hide the scrollbar
        textArea.style.height = `${scrollHeight}px`
        textArea.style.overflowY = 'hidden'
      }
    }
  }, [textAreaRef, value, maxHeight])
}
